import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import mapValues from 'lodash/mapValues'
import parse from 'html-react-parser'
import classnames from 'classnames'
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { getSizeName } from 'services/SizesManager'
import { useKeys } from 'services/KeysProvider'
import { getPricingCountry } from 'services/CountryManager'
import { useProductType } from '../../services/ProductTypesManager'
import useScreenSize from '../../hooks/useScreenSize'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import type { QuestionProps } from './consts'
import * as S from './FaqSection.styles'

function ExpandIcon({ expanded }: { expanded: boolean }) {
  return (
    <div className="wrap">
      <span className={classnames('arrow', !expanded && 'active')}>
        <span />
        <span />
      </span>
    </div>
  )
}

export default function FaqSection({
  faqs,
  source,
}: {
  faqs: QuestionProps[]
  source: string
}) {
  const { t } = useTranslation()
  const { productType } = useProductType()
  const isMediumScreen = useScreenSize('md')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const keys = useKeys()
  const pricingCountry = getPricingCountry(keys.ipCountry)

  const translatedFaqs = faqs.map(({ translateParams, ...faq }) => ({
    ...faq,
    translateParams: mapValues(
      translateParams || {},
      (size) => getSizeName(size, { t, country: pricingCountry }) ?? size
    ),
  }))

  const handleFaqViewed = () => {
    analytics.track(
      productType === PRODUCT_TYPES.PHOTO_BOOK
        ? EVENT_NAMES.BOOK_INTRO_HOMEPAGE_FAQ_VIEWED
        : EVENT_NAMES.HOMEPAGE_FAQ_VIEWED,
      { Source: source }
    )
  }

  const handleFaqTapped = ({
    translateKey,
    isExpanded,
  }: {
    translateKey: string
    isExpanded: boolean
  }) => {
    // For analytics, always use English version
    const englishQuestion = t(`homepage.faq.${translateKey}.title`, {
      lng: 'en',
    })
    analytics.track(
      productType === PRODUCT_TYPES.PHOTO_BOOK
        ? EVENT_NAMES.BOOK_INTRO_HOMEPAGE_FAQ_TAPPED
        : EVENT_NAMES.HOMEPAGE_FAQ_TAPPED,
      {
        Question: englishQuestion,
      }
    )
    setExpandedId(isExpanded ? translateKey : null)
  }

  return (
    <S.FaqVisibilityDetector onVisible={handleFaqViewed}>
      <S.Container>
        <S.TextContainer itemScope itemType="https://schema.org/FAQPage">
          <S.Title>
            {isMediumScreen ? t('general.menu.faq') : t('homepage.faq.title')}
          </S.Title>
          <S.QuestionsContainer id="faq-section">
            {translatedFaqs.map(({ translateKey, translateParams = {} }) => (
              <S.QuestionListItem
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                key={translateKey}
                onClick={() =>
                  handleFaqTapped({
                    translateKey,
                    isExpanded: expandedId !== translateKey,
                  })
                }
              >
                <Accordion
                  key={translateKey}
                  expanded={expandedId === translateKey}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandIcon expanded={expandedId === translateKey} />
                    }
                    itemProp="name"
                  >
                    {t(`homepage.faq.${translateKey}.title`, translateParams)}
                  </AccordionSummary>
                  <AccordionDetails
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <div itemProp="text">
                      {parse(
                        t(
                          `homepage.faq.${translateKey}.content`,
                          translateParams
                        )
                      )}
                    </div>
                  </AccordionDetails>
                </Accordion>
              </S.QuestionListItem>
            ))}
          </S.QuestionsContainer>
        </S.TextContainer>
      </S.Container>
    </S.FaqVisibilityDetector>
  )
}
